import { A11yModule } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, inject } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideClock,
  lucideExternalLink,
  lucideNewspaper,
  lucideNotebookPen,
} from '@ng-icons/lucide';
import { BrnSheetContentDirective, BrnSheetTriggerDirective } from '@spartan-ng/brain/sheet';
import { HlmBadgeDirective } from '@spartan-ng/ui-badge-helm';
import { HlmButtonDirective } from '@spartan-ng/ui-button-helm';
import { HlmIconDirective } from '@spartan-ng/ui-icon-helm';
import {
  HlmSheetComponent,
  HlmSheetContentComponent,
  HlmSheetHeaderComponent,
  HlmSheetTitleDirective,
} from '@spartan-ng/ui-sheet-helm';
import { NewsArticle, NewsCardThemeColor } from '../../models/news-article.model';
import { ExtractDomainPipe } from '../../pipes/extract-domain.pipe';
import { HighlightSearchPipe } from '../../pipes/highlight-search.pipe';

// Define theme color mappings
const THEME_COLOR_CLASSES: Record<NewsCardThemeColor, { light: string; dark: string }> = {
  rose: { light: 'bg-rose-100', dark: 'bg-[#893f45]/80' },
  pink: { light: 'bg-pink-100', dark: 'bg-pink-950/80' },
  fuchsia: { light: 'bg-fuchsia-100', dark: 'bg-[#5b3256]/80' },
  purple: { light: 'bg-purple-100', dark: 'bg-[#301934]/80' },
  violet: { light: 'bg-violet-100', dark: 'bg-[#321746]/80' },
  indigo: { light: 'bg-indigo-100', dark: 'bg-[#1e2952]/80' },
  blue: { light: 'bg-blue-100', dark: 'bg-blue-950/80' },
  sky: { light: 'bg-sky-100', dark: 'bg-sky-950/80' },
  emerald: { light: 'bg-emerald-100', dark: 'bg-emerald-950/80' },
  green: { light: 'bg-green-100', dark: 'bg-[#3b3c36]/80' },
  lime: { light: 'bg-lime-100', dark: 'bg-[#4a412a]/80' },
  amber: { light: 'bg-amber-100', dark: 'bg-[#44362f]/80' },
  orange: { light: 'bg-orange-100', dark: 'bg-[#722f37]/80' },
};

@Component({
  selector: 'app-news-card',
  imports: [
    CommonModule,
    RouterLink,
    A11yModule,
    ExtractDomainPipe,
    HighlightSearchPipe,
    BrnSheetTriggerDirective,
    BrnSheetContentDirective,
    HlmSheetComponent,
    HlmSheetContentComponent,
    HlmSheetHeaderComponent,
    HlmSheetTitleDirective,
    HlmButtonDirective,
    HlmIconDirective,
    NgIcon,
    HlmBadgeDirective,
  ],
  templateUrl: './news-card.component.html',
  styleUrl: './news-card.component.scss',
  providers: [provideIcons({ lucideClock, lucideArrowRight, lucideExternalLink, lucideNotebookPen, lucideNewspaper })],
})
export class NewsCardComponent implements OnInit {
  @Input() article!: NewsArticle;
  @Input() searchTerm = '';

  private router = inject(Router);

  // Properties to hold the selected Tailwind classes
  selectedBgClassLight = '';
  selectedBgClassDark = '';

  ngOnInit() {
    // Get color classes based on the article's theme
    const colorPair = THEME_COLOR_CLASSES[this.article.image.theme];
    this.selectedBgClassLight = colorPair.light;
    this.selectedBgClassDark = colorPair.dark;
  }

  navigateToArticle() {
    this.router.navigate(['/news', this.article.slug]);
  }
}
